import React from 'react';
import { Box, Typography, Paper, Grid, Chip } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import StorageIcon from '@mui/icons-material/Storage';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ShieldIcon from '@mui/icons-material/Shield';
import AssessmentIcon from '@mui/icons-material/Assessment';

const HomePage = () => {
  const moduleStyle = {
    p: 2.5,
    height: '100%',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    transition: 'all 0.2s',
    cursor: 'default',
    '&:hover': { borderColor: '#1abc9c', boxShadow: '0 2px 8px rgba(26,188,156,0.15)' }
  };

  return (
    <Box sx={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Hero */}
      <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 100%)', borderRadius: '12px', color: 'white' }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          📊 Credit Risk Modelling Platform
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, opacity: 0.9, maxWidth: '800px' }}>
          A comprehensive toolkit for regulatory credit risk modelling under IFRS 9 and Basel frameworks. 
          Build, calibrate, and validate <strong>LGD</strong> (Loss Given Default) and <strong>CCF</strong> (Credit Conversion Factor) 
          models with advanced statistical techniques.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label="IFRS 9" sx={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 500 }} />
          <Chip label="Basel III/IV" sx={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 500 }} />
          <Chip label="EBA Guidelines" sx={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 500 }} />
        </Box>
      </Paper>

      {/* What is LGD & CCF */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
        Understanding LGD & CCF
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={moduleStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#e74c3c', mb: 1 }}>
              💰 LGD — Loss Given Default
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#555', lineHeight: 1.7 }}>
              <strong>LGD</strong> represents the percentage of exposure a bank loses when a borrower defaults. 
              It is the complement of the <strong>Recovery Rate</strong>:
            </Typography>
            <Paper sx={{ p: 1.5, background: '#fdf2f2', mb: 1.5, textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#c0392b', borderRadius: '6px' }}>
              LGD = 1 − Recovery Rate
            </Paper>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
              <strong>Example:</strong> A loan of €100,000 defaults. The bank recovers €35,000 through collateral and collections.
              Recovery Rate = 35% → <strong>LGD = 65%</strong>
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip label="Downturn LGD" size="small" sx={{ mr: 0.5, background: '#fde8e8', color: '#c0392b' }} />
              <Chip label="Long-run Average LGD" size="small" sx={{ mr: 0.5, background: '#fde8e8', color: '#c0392b' }} />
              <Chip label="MoC" size="small" sx={{ background: '#fde8e8', color: '#c0392b' }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={moduleStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#2980b9', mb: 1 }}>
              📐 CCF — Credit Conversion Factor
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#555', lineHeight: 1.7 }}>
              <strong>CCF</strong> measures the proportion of an undrawn commitment that is expected to 
              be drawn at the time of default. It converts off-balance sheet exposure to on-balance sheet:
            </Typography>
            <Paper sx={{ p: 1.5, background: '#eaf2f8', mb: 1.5, textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#2471a3', borderRadius: '6px' }}>
              EAD = Drawn + CCF × Undrawn
            </Paper>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
              <strong>Example:</strong> A credit line of €200,000 has €50,000 drawn and €150,000 undrawn.
              If CCF = 40%, then EAD = €50,000 + 40% × €150,000 = <strong>€110,000</strong>
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip label="EAD" size="small" sx={{ mr: 0.5, background: '#d4e6f1', color: '#2471a3' }} />
              <Chip label="Cohort CCF" size="small" sx={{ mr: 0.5, background: '#d4e6f1', color: '#2471a3' }} />
              <Chip label="Risk Quantification" size="small" sx={{ background: '#d4e6f1', color: '#2471a3' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Platform Modules */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
        🛠 Platform Modules
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { icon: <StorageIcon />, title: 'Import & Data', desc: 'Upload datasets (CSV, Excel, SAS, RDS). Preview, sort, wrangle, and transform data with built-in tools.', color: '#3498db' },
          { icon: <AnalyticsIcon />, title: 'Data Quality & Discretization', desc: 'Missing value analysis & imputation. Expert cut, isotonic regression, monotonic binning, quantile cut.', color: '#2ecc71' },
          { icon: <AssessmentIcon />, title: 'Extremes Values', desc: 'IQR, Isolation Forest, and Z-Score methods for outlier detection and treatment.', color: '#e74c3c' },
          { icon: <AssessmentIcon />, title: 'Risk Differentiation', desc: 'Sampling, CART decision trees, and performance testing for risk driver identification.', color: '#1abc9c' },
          { icon: <TimelineIcon />, title: 'Max Recovery Period (MRP)', desc: 'Cash-flow and number-based MRP calibration for recovery period estimation.', color: '#f39c12' },
          { icon: <TimelineIcon />, title: 'CF Projection', desc: 'Additive and multiplicative models for recovery curve projection by grade and segment.', color: '#e67e22' },
          { icon: <ShieldIcon />, title: 'Risk Quantification', desc: 'LRA (Loss Rate Analysis), MoC C, Downturn identification & quantification for regulatory LGD.', color: '#9b59b6' },
        ].map((mod, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Paper sx={moduleStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ color: mod.color, fontSize: '28px' }}>{mod.icon}</Box>
                <Typography variant="subtitle1" fontWeight={600}>{mod.title}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>{mod.desc}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Quick Workflow */}
      <Paper sx={{ p: 3, background: '#f9fbfd', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
          ⚡ Typical Modelling Workflow
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          {['1. Upload Data', '→', '2. Data Quality', '→', '3. Discretization', '→', '4. Extremes Values', '→', '5. Risk Diff (CART)', '→', '6. MRP', '→', '7. CF Projection', '→', '8. Risk Quantification', '→', '9. Export & Report'].map((step, i) => (
            <Chip 
              key={i} 
              label={step} 
              sx={{ 
                fontWeight: step.includes('.') ? 600 : 400, 
                background: step.includes('.') ? '#1abc9c' : 'transparent', 
                color: step.includes('.') ? 'white' : '#95a5a6',
                fontSize: step.includes('.') ? '12px' : '16px',
                px: step.includes('.') ? 1.5 : 0.5
              }} 
            />
          ))}
        </Box>
      </Paper>

    </Box>
  );
};

export default HomePage;
